// Exports the F7 app object
import '../../stylesheets/index.less';

import * as Auth                     from '../auth';
import * as ENV                      from '../util/env';
import * as QS                       from 'querystring';
import * as User                     from '../user';
import { ForceOpenInApp }            from './force_open_in_app';
import { onDeepLink }                from './deep_links';
import { shouldOpenInApp }           from './force_open_in_app';
import { Statusbar }                 from 'framework7-react';
import { Provider as StoreProvider } from 'react-redux';
import AppProvider                   from './app_provider';
import CallBar                       from '../call';
import ErrorBoundary                 from '../components/error_boundary';
import F7Event                       from './events/f7_event';
import F7Listener                    from '../components/f7_listener';
import Impersonate                   from '../user/impersonate';
import lockOrientationInMobile       from './lock_orientation_in_mobile';
import MainView                      from './main_view';
import ms                            from 'ms';
import Notification                  from '../components/notification';
import OfflineIndicator              from '../components/offline_indicator';
import onChange                      from '../util/on_change';
import ProgressIndicator             from '../components/progress_indicator';
import React                         from 'react';
import showSignInPage                from './sign_in';
import showStartupErrorPage          from './startup_error';
import store                         from '../store';
import Toast                         from '../components/toast';
import WindowTitle                   from '../components/window_title';

import './access_token';
import './deep_links';
import './keyboard';


export default class BroadlyApp extends React.PureComponent {

  constructor(props) {
    super(props);

    this.start = this.start.bind(this);
    this.state = {
      isReady:         false,
      shouldOpenInApp: false
    };
  }

  async componentDidMount() {
    this.passwordlessOnDeepLink();
    await this.start();
  }

  passwordlessOnDeepLink() {
    onDeepLink(function({ pathname, search }) {
      attemptPasswordlessAuthentication({ pathname, search });
    });
  }

  // The start up sequence:
  // - If user not authenticated, show sign in page
  // - Once user authenticated, let them access the app
  // - If there's an error, show message and have use retry
  async start() {
    try {
      ensureLocationHref();
      ensurePushStateSeparator();

      await lockOrientationInMobile();

      if (shouldOpenInApp())
        this.setState({ shouldOpenInApp: true });
      else {
        await authenticate();
        await waitUntilSignedIn();
      }

      await hideSplashscreen();
      this.setState({ isReady: true });
    } catch (error) {
      showStartupErrorPage({
        error,
        retry: () => {
          F7Listener.clearPendingEvents();
          this.start();
        }
      });
      hideSplashscreen();
    }
  }

  render() {
    if (!this.state.isReady)
      return null;

    if (this.state.shouldOpenInApp)
      return <ForceOpenInApp/>;

    return (
      <StoreProvider store={store}>
        <ErrorBoundary>
          <AppProvider>
            <Statusbar/>
            <WindowTitle/>
            <Impersonate/>
            <Toast/>
            <ProgressIndicator/>
            <F7Listener/>
            <Notification/>
            <MainView/>
            <OfflineIndicator/>
            <CallBar/>
          </AppProvider>
        </ErrorBoundary>
      </StoreProvider>
    );
  }
}


async function attemptPasswordlessAuthentication({ pathname, search }) {
  const isPasswordlessPath = pathname.includes('/signin/passwordless');
  const userHasToken       = User.getJWTToken(store.getState());

  if (!isPasswordlessPath)
    return;

  // if the user is already logged in we avoid doing the authentication again
  if (userHasToken)
    return;

  const query           = search.substr(search.indexOf('?') + 1);
  const params          = QS.parse(query);
  const { username }    = params;
  const { token }       = params;
  const { redirectTo }  = params;
  const canAuthenticate = !!username && !!token;
  if (canAuthenticate)
    await store.dispatch(User.authenticate({ username, password: token }));

  const hasAccessToken = !!User.getAccessToken(store.getState());
  if (hasAccessToken)
    await store.dispatch(User.loadUserProfile());


  document.body.dispatchEvent(new F7Event(app => {
    // TODO: This logic belongs inside the signin page.
    // signing page could be open, let's close it
    if (app.popup)
      app.popup.close();

    if (redirectTo)
      app.views.main.router.navigate(redirectTo, { reloadCurrent: true });
  }));
}


function ensureLocationHref() {
  // Only do this on mobile (looking into https://rollbar.com/broadly/app/items/4194)
  if (!ENV.isCordova())
    return;

  // Starting with ionic-webview 5.x, document.location
  // is ionic://localhost on iOS and http://localhost on
  // Android.
  //
  // Framework7 3.x uses the following code to get
  // the "document URL":
  //
  //   document.location.href.split(document.location.origin)[1];
  //
  //   https://github.com/framework7io/framework7/blob/bb3d9bd77d18f98e3bce25eb8bafb9304bb6f055/src/core/modules/router/router-class.js#L1190
  //
  // With document.location = 'ionic://localhost', the above line
  // evaluates to '' and this breaks Framework7 routing.
  //
  // Here we ensure that the location ends with a slash.
  //
  // Hopefully this is fixed in newer versions of Framework7
  // and we can remove this function when we upgrade.

  const documentURLForFramework7 = document.location.href.split(document.location.origin)[1];
  if (!documentURLForFramework7)
    window.history.pushState({}, null, '/');
}

function ensurePushStateSeparator() {
  const { pathname } = window.location;

  if (pathname === '/')
    return;

  window.history.pushState({}, null, `/#${pathname}`);
}


async function authenticate() {
  if (Auth.needsSalseforceAuthentication()) {
    // Redirect browser, function will never return
    Auth.authenticateWithOAuth('salesforce');
  } else {
    // Get access token from OAuth flow, or SMS link
    if (Auth.isOAuthCallback())
      await onOAuthCallback();
    if (Auth.isPasswordlessAuthentication())
      await onPasswordlessAuthentication();

    // Make sure user authenticated by loading their profile
    // This is the ultimate check: valid access token + user account exists
    const hasAccessToken = !!User.getAccessToken(store.getState());
    if (hasAccessToken)
      await store.dispatch(User.loadUserProfile());
  }
}

// Handles OAuth callback (authenticating without username and password, e.g. Salesforce).
async function onOAuthCallback() {
  const { accessToken, returnURL } = Auth.getOAuthResult();

  await store.dispatch(User.signIn(accessToken));

  window.history.pushState({}, null, returnURL);
}

// Handled passwordless authentication, SMS link contains username + password
async function onPasswordlessAuthentication() {
  const { username, password } = Auth.getPasswordlessCredentials();
  const redirectTo             = getQueryStringValue('redirectTo');
  try {
    await store.dispatch(User.authenticate({ username, password }));
    window.history.pushState({}, null, redirectTo ? `#${redirectTo}` : '/');
  } catch (error) {
    // could not authenticate render normal page
  }
}

function getQueryStringValue(key) {
  const { hash } = window.location;
  const query    = hash.substr(hash.indexOf('?') + 1);
  const parsedQS = QS.parse(query);
  return parsedQS[key];
}

async function hideSplashscreen() {
  await waitForResources();
  hideSplashscreenForWeb();
  hideSplashscreenForCordova();
}

// Web app uses an inline SVG element so it renders before the browser got to
// process any CSS or JS.
function hideSplashscreenForWeb() {
  const launchScreen = document.querySelector('.b-launch-screen');
  if (launchScreen)
    launchScreen.style.display = 'none';
}

// Cordova uses a plugin to show the splash screen (on iOS this is known as
// launch screen, and will show when the app starts, before any other assets are
// loaded).
function hideSplashscreenForCordova() {
  if (navigator.splashscreen)
    navigator.splashscreen.hide();
  fixAndroidStatusBar();
}

function fixAndroidStatusBar() {
  // These should be set in config.xml, but Android + PGB seems to ignore config.xml
  // - If statusbar overlays webview (the default), then opening the keyboard
  //   will not resize the view port, and the keyboard obscures the message box
  // - The statusbar color is set to white, to blend with the app
  // - Apparently the default style is not the default, so we have to set it
  //   explicitly
  const { StatusBar, device } = window;
  if (device && device.platform === 'Android' && StatusBar) {
    StatusBar.hide();
    StatusBar.overlaysWebView(false);
    // wait a bit to webview change, and so show statusbar
    setTimeout(() => {
      StatusBar.backgroundColorByName('white');
      StatusBar.styleDefault();
      StatusBar.show();
    }, 100);
  }
}

// Listeners
document.addEventListener('deviceready', function() {
  fixAndroidStatusBar();
}, false);

// Update lastActive + profile when resume
document.addEventListener('resume', function() {
  store.dispatch(User.silentlyLoadUserProfile());
}, false);



// Wait for resources (CSS, fonts, etc) to load before we get anything started.
// We need these before we can hide the splashscreen, or so the sign in screen.
async function waitForResources() {
  if (document.readyState === 'complete')
    return;

  await new Promise(function(resolve) {
    window.addEventListener('load', resolve);
    setTimeout(resolve, ms('3s'));
  });
}


// If user has not signed in, show them the sign in screen, and wait for them to
// sign in.  If user is signed in, return immediately.
async function waitUntilSignedIn() {
  if (User.isSignedIn(store.getState()))
    return;

  showSignInPage();
  await hideSplashscreen();
  await onChange(store, User.isSignedIn);
}
